'use client'

import type { FAQStatBlock } from '@/lib/c2c/types'
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion'

interface FAQItem {
    question: string
    answer: string
    stats?: FAQStatBlock[]
}

interface FAQSectionProps {
    title?: string
    items: FAQItem[]
}

function StatBlocks({ stats }: { stats: FAQStatBlock[] }) {
    return (
        <div className="flex flex-row gap-8 md:flex-col md:gap-6">
            {stats.map((stat) => (
                <div key={stat.label} className="text-left md:text-center">
                    <p className="text-xs font-bold tracking-[1.4px] text-[#6c7078] uppercase">
                        {stat.label}
                    </p>
                    <p className="mt-1 text-[28px] leading-tight font-bold text-[#44474a]">
                        {stat.value}
                    </p>
                </div>
            ))}
        </div>
    )
}

/**
 * Displays FAQ accordion for C2C page.
 * Returns null when no FAQ items are available.
 */
export function FAQSection({ title = 'Frequently Asked Questions', items }: FAQSectionProps) {
    if (items.length === 0) {
        return null
    }

    return (
        <section data-section="faq" className="my-[72px] md:my-24">
            <h2 className="mb-8 text-2xl font-bold text-[#44474a]">{title}</h2>
            <div className="rounded-[7px] border border-[#d5dde6]">
                <Accordion
                    type="multiple"
                    defaultValue={items.map((_, i) => `faq-${String(i)}`)}
                    className="w-full"
                >
                    {items.map((item, index) => (
                        <AccordionItem
                            key={index}
                            value={`faq-${index}`}
                            className="border-[#d5dde6]"
                        >
                            <AccordionTrigger className="px-4 py-5 text-left text-base font-bold [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-[#2e3033]">
                                {item.question}
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4 md:px-6 md:pb-6">
                                {item.stats ? (
                                    <div className="flex flex-col gap-6 md:flex-row md:gap-10">
                                        <div className="flex-1">
                                            <p className="text-base leading-6 text-[#44474a]">
                                                {item.answer}
                                            </p>
                                        </div>
                                        <div className="md:w-[200px] md:shrink-0">
                                            <StatBlocks stats={item.stats} />
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-base leading-6 text-[#44474a]">
                                        {item.answer}
                                    </p>
                                )}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    )
}
